/**
 * Until 0.8.0 every address of the interface lived after a `#`
 * (`/#/contents/abc`), and those addresses are in bookmarks, in chat threads
 * and in the gateway's own redirects (`/#/login?error=…` after a failed
 * sign-in). This turns each of them into its clean equivalent so nothing that
 * worked before stops working.
 *
 * Old site-scoped addresses carried the site as `?site=`; when they did not,
 * the last site the person worked on is used, and without one the home page
 * is the honest answer. A content id alone goes to `/contenuti/:id`, which
 * finds its site and redirects: the old URL never said which site it was on.
 */
import { paths, type SiteSection } from '../routes.js'

const LEGACY_SITE_SECTIONS: Record<string, SiteSection> = {
  menu: 'menu',
  theme: 'tema',
  sections: 'sezioni',
  pages: 'pagine',
  listings: 'elenchi',
  publications: 'pubblicazioni',
}

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/** `null` when the hash is not an old route (an in-page anchor, or nothing). */
export function legacyHashTarget(hash: string, fallbackSiteId: string | null): string | null {
  if (!hash.startsWith('#/')) return null
  const raw = hash.slice(1)
  const question = raw.indexOf('?')
  const path = question < 0 ? raw : raw.slice(0, question)
  const params = new URLSearchParams(question < 0 ? '' : raw.slice(question + 1))
  const segments = path.split('/').filter(Boolean).map(decode)
  const siteId = params.get('site') || fallbackSiteId
  const [first, second, third] = segments

  switch (first) {
    case undefined:
      return paths.home()
    case 'login': {
      const error = params.get('error')
      return error ? `/accesso?error=${encodeURIComponent(error)}` : '/accesso'
    }
    case 'contents': {
      if (!second) {
        if (!siteId) return paths.home()
        return paths.contents(siteId, {
          type: params.get('type') ?? undefined,
          status: params.get('status') ?? undefined,
          q: params.get('search') ?? undefined,
        })
      }
      if (second === 'new') return siteId ? paths.newContent(siteId) : paths.home()
      const upTo = params.get('upTo')
      const view = third === 'page' ? 'pagina' : third === 'history' ? 'storia' : undefined
      return paths.contentAnySite(second, view, upTo !== null && /^\d+$/.test(upTo) ? Number(upTo) : undefined)
    }
    case 'site': {
      if (!siteId) return paths.home()
      if (!second) return paths.settings(siteId)
      const section = LEGACY_SITE_SECTIONS[second]
      return section ? paths.siteSection(siteId, section) : paths.site(siteId)
    }
    case 'knowledge':
      return paths.knowledge()
    case 'people':
      return paths.people()
    case 'sites':
      return second === 'new' ? paths.newSite() : paths.home()
    case 'releases':
      return paths.releaseNotes()
    default:
      return paths.home()
  }
}
